import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLoginPrivilegeInfo } from '../../../../api/auth/auth';
import { Ps, UserPrivilege } from '../../../../api/model';
import {
  usePostPsDelete,
  usePostPsDownloadAdd,
  usePostPsDownloadBatch,
  usePostPsDownloadCancel,
  usePostPsDownloadCheck,
} from '../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../hooks/api';
import { useCounter } from '../../../../hooks/useCounter';
import { getAxiosRequestConfig, getAxiosRequestConfigForBinary } from '../../../../utils/api';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import { toString } from '../../../../utils/string';
import ErrorComponent from '../../../atoms/ErrorComponent';
// PS 追加・編集画面 → 他の画面へ遷移時にワーニングを表示するため、atoms/Link を使用する。
import Link from '../../../atoms/Link';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import { PsDetailsMode } from './constants/types';

interface Props {
  /** PS 閲覧・追加・編集の内、どのモードかを示す。 */
  mode: PsDetailsMode;

  // /** このコンポーネントで操作の対象となる PS のバージョン ID。 */
  // psVersionId: number | undefined;

  /** 操作の対象となる PS。 */
  targetPs: Ps | undefined;

  /** URL で指定された PS バージョン ID。 */
  psVersionIdParam: number | undefined;

  /** PsDetailsPage の state 経由で PsTree で設定される、PS 出力・原作用 PS 出力用の、出力対象となる、現時点での PS ツリーの search_result = true となっている PS の バージョン ID の配列。 */
  psVersionIdsToDownload: number[];

  /** (PS ツリー) 表示オプション ダイアログ表示・非表示状態設定関数。 */
  setIsPsTreeDisplayOptionsDialogOpen: (isOpen: boolean) => void;

  // /** PS ツリーを再検索 (postPsTreeSearch) するか否か。 */
  // doRePostPsTreeSearch: boolean;
  /** PS ツリーを再検索 (postPsTreeSearch) するか設定する関数。 */
  setDoRePostPsTreeSearch: (doRePostPsTreeSearch: boolean) => void;

  /** PS 選択状態を解除するかどうかを設定する関数。 */
  setDoClearSelectedPsVersionIds: (doClearSelectedPsVersionIds: boolean) => void;

  // /** PsDetailsPage の state、doSavePs を設定する関数。 */
  // setDoSavePs: (doSavePs: boolean) => void;

  /** 一括選択用の表示言語の state。0: 日本語、1: 米語、2: 英語。  */
  bulkDisplayLanguages: boolean[];

  /** 表示言語のstateを一括で設定する関数。 */
  setBulkDisplayLanguages: (bulkDisplayLanguages: boolean[]) => void;

  /** PS出力可能かどうか(PSリストを取得するまでPS出力リンクを無効化) */
  canPsDownload: boolean;
}

// PS 閲覧・追加・編集画面の上部のボタン類。
function PsDetailsPageToolbar({
  mode,
  // psVersionId,
  targetPs,
  psVersionIdParam,
  psVersionIdsToDownload,
  setIsPsTreeDisplayOptionsDialogOpen,
  // doRePostPsTreeSearch,
  setDoRePostPsTreeSearch,
  setDoClearSelectedPsVersionIds,
  // setDoSavePs,
  bulkDisplayLanguages,
  setBulkDisplayLanguages,
  canPsDownload,
}: Props) {
  // i18n 用。
  const { t, i18n } = useTranslation();

  const defaultOptions = useDefaultOptions(i18n.language);
  const axiosConfig = getAxiosRequestConfig(i18n.language);
  const axiosConfigForBinary = getAxiosRequestConfigForBinary(i18n.language);

  // console.log('targetPs', targetPs);

  // ルートPSと章PS
  const isL0L1Ps = useMemo(() => (targetPs === undefined ? true : Number(targetPs.type) <= 1), [targetPs]);

  /** PS 廃止 API。 */
  const postPsDeleteMutation = usePostPsDelete({
    axios: axiosConfig,
  });

  // MEMO: ダイアログで排他処理をするので、「PS出力」か「原作用PS出力」ボタンが押下された後、完了 / キャンセルまで同じボタンが再び押下されることや、もう片方のボタンが押下されることはない、という前提で実装している。一連の API 呼び出しは 1 つの出力プロセスに対応していればよい。

  /** PS 出力中か否か。 */
  const [isPsDownloadBatchInProgress, setIsPsDownloadBatchInProgress] = useState<boolean>(false);

  /** PS 情報 Excel ファイル生成バッチ処理ジョブ登録 API。 */
  const postPsDownloadAddMutation = usePostPsDownloadAdd({
    axios: axiosConfig,
  });

  /** usePostPsDownloadAdd の返り値 (postPsDownloadAddMutation.data.data) のバッチ ID を保存する state。undefined じゃなければ出力中。 */
  const [psExportBatchId, setPsExportBatchId] = useState<string | undefined>(undefined);
  // const batchId = postPsDownloadAddMutation.data?.data;
  // const batchIdRef = useRef<string | undefined>(undefined);

  // console.log('psExportBatchId:', psExportBatchId);

  // カウンター フック。
  // TODO?: 設定ファイルで変えられるようにする？
  /** 1 秒ごとのカウント。 */
  const { count, setDoCount } = useCounter(1000, false);

  /** useEffect() 内の関数で変更される、以前にその関数を実行した時の count の値。以前の実行以降 count が変化したかどうかの判断に使用。 */
  const [previousCount, setPreviousCount] = useState<number>(0);

  /** PS 情報 Excel ファイル ダウンロード チェック API。 */
  const postPsDownloadCheckMutation = usePostPsDownloadCheck({
    axios: axiosConfig,
  });

  /** 以前の postPsDownloadCheckMutation の status。比較用。「PS出力」/「原作用PS出力」ボタンが押下されたのが 2 回目以降の場合に対応するため。 */
  const [postPsDownloadCheckMutationPreviousStatus, setPostPsDownloadCheckMutationPreviousStatus] =
    useState<string>('idle');

  /** 出力された PS のダウンロード可否。 */
  const [isReadyForDownloadPs, setIsReadyForDownloadPs] = useState<boolean>(false);
  // const isReadyForDownloadPs = postPsDownloadCheckMutation.data?.data === 1;

  // console.log('isReadyForDownloadPs:', isReadyForDownloadPs);

  /** PS 出力中に関係する states をリセットする関数。 */
  const resetPsDownloadBatchStates = useCallback(() => {
    // PS 出力プロセスの終了。
    setIsPsDownloadBatchInProgress(false);

    // バッチ ID をリセット。
    setPsExportBatchId(undefined);

    // カウント停止 → チェックも停止。
    setDoCount(false);
    setPreviousCount(0);

    // status をリセット。
    setPostPsDownloadCheckMutationPreviousStatus('idle');
    // ダウンロード不可とする。
    setIsReadyForDownloadPs(false);
  }, [setDoCount]);

  // PS 情報 Excel ファイル生成バッチ処理ジョブ登録 API が成功時、カウントを開始する。
  useEffect(() => {
    if (
      // 一連の PS 出力プロセスの間。
      isPsDownloadBatchInProgress &&
      postPsDownloadAddMutation.isSuccess &&
      typeof postPsDownloadAddMutation.data?.data === 'string' &&
      psExportBatchId !== postPsDownloadAddMutation.data.data
    ) {
      // バッチ ID を保存。
      setPsExportBatchId(postPsDownloadAddMutation.data.data);
      // batchIdRef.current = postPsDownloadAddMutation.data.data;

      // カウントを開始。
      setDoCount(true);
      setPreviousCount(0);

      // status をリセット (一応)。
      setPostPsDownloadCheckMutationPreviousStatus('idle');
      // ダウンロード可否をリセット (一応)。
      setIsReadyForDownloadPs(false);
    }
  }, [
    isPsDownloadBatchInProgress,
    postPsDownloadAddMutation.data?.data,
    postPsDownloadAddMutation.isSuccess,
    psExportBatchId,
    setDoCount,
  ]);

  // PS 情報 Excel ファイル ダウンロード チェック API を呼ぶ。
  useEffect(() => {
    if (
      typeof psExportBatchId === 'string' &&
      // まだダウンロード可になっておらず、
      !isReadyForDownloadPs &&
      // カウントが変わっていた場合。
      count !== previousCount
    ) {
      // 新しいカウントを登録。
      setPreviousCount(count);

      // ダウンロード可否を判断する。
      postPsDownloadCheckMutation.mutate({
        batchId: psExportBatchId,
      });
    }
  }, [count, isReadyForDownloadPs, postPsDownloadCheckMutation, previousCount, psExportBatchId]);

  /** PS 情報 Excel ファイル ダウンロード API。 */
  const postPsDownloadBatchMutation = usePostPsDownloadBatch<AxiosError<Blob>>({
    axios: axiosConfigForBinary,
  });

  useEffect(() => {
    if (
      typeof psExportBatchId === 'string' &&
      // loading から
      postPsDownloadCheckMutationPreviousStatus === 'loading' &&
      // success になった時。
      // (「PS出力」/「原作用PS出力」ボタンが押下されたのが 2 回目以降の場合に対応)
      postPsDownloadCheckMutation.status === 'success' &&
      // ダウンロード可になった。
      postPsDownloadCheckMutation.data?.data === 1
    ) {
      // ダウンロード可になった。
      setIsReadyForDownloadPs(true);

      // カウント停止 → チェックも停止。
      setDoCount(false);
      setPreviousCount(0);

      // ダウンロード用 API 実行。
      postPsDownloadBatchMutation.mutate({
        batchId: psExportBatchId,
      });
    }

    if (postPsDownloadCheckMutationPreviousStatus !== postPsDownloadCheckMutation.status) {
      // 比較のために保存。
      setPostPsDownloadCheckMutationPreviousStatus(postPsDownloadCheckMutation.status);
    }
  }, [
    postPsDownloadBatchMutation,
    postPsDownloadCheckMutation.data?.data,
    postPsDownloadCheckMutation.status,
    postPsDownloadCheckMutationPreviousStatus,
    psExportBatchId,
    setDoCount,
  ]);

  useEffect(() => {
    if (
      // ダウンロード用 API 成功時。
      postPsDownloadBatchMutation.isSuccess &&
      // データが届いた。
      postPsDownloadBatchMutation.data
    ) {
      // ダウンロード。
      executeDownload(postPsDownloadBatchMutation.data as AxiosResponse<Blob, unknown>);
    }
  }, [postPsDownloadBatchMutation.data, postPsDownloadBatchMutation.isSuccess, resetPsDownloadBatchStates]);

  // エラー等の対応。
  useEffect(() => {
    if (
      postPsDownloadAddMutation.isError ||
      postPsDownloadAddMutation.isPaused ||
      postPsDownloadCheckMutation.isError ||
      postPsDownloadCheckMutation.isPaused ||
      postPsDownloadBatchMutation.isError ||
      postPsDownloadBatchMutation.isPaused
    ) {
      // states をリセット。
      resetPsDownloadBatchStates();
    }
  }, [
    postPsDownloadAddMutation.isError,
    postPsDownloadAddMutation.isPaused,
    postPsDownloadBatchMutation.isError,
    postPsDownloadBatchMutation.isPaused,
    postPsDownloadCheckMutation.isError,
    postPsDownloadCheckMutation.isPaused,
    resetPsDownloadBatchStates,
  ]);

  /** PS 情報 Excel 生成バッチ処理キャンセル API。 */
  const postPsDownloadCancelMutation = usePostPsDownloadCancel({
    axios: axiosConfig,
  });

  /** PS 出力ファイル種別。>= 0 で確認ダイアログの表示、< 0 で非表示。 */
  const [psDownloadFileType, setPsDownloadFileType] = useState<number>(-1);

  /** PS ツリーの再検索を実行したか否か。 */
  const [didSetDoRePostPsTreeSearch, setDidSetDoRePostPsTreeSearch] = useState<boolean>(false);

  // console.log('didSetDoRePostPsTreeSearch: ', didSetDoRePostPsTreeSearch);

  useEffect(() => {
    if (
      // PS 廃止が成功した場合。
      postPsDeleteMutation.isSuccess &&
      // 無限ループにならないように、↓ の条件が必要か？
      !didSetDoRePostPsTreeSearch
    ) {
      // PS ツリーの再検索を実行する。
      setDoRePostPsTreeSearch(true);
      // PS ツリーの再検索を実行した。
      setDidSetDoRePostPsTreeSearch(true);
      // PS 選択状態を解除する。
      setDoClearSelectedPsVersionIds(true);
    }
  }, [postPsDeleteMutation, didSetDoRePostPsTreeSearch, setDoClearSelectedPsVersionIds, setDoRePostPsTreeSearch]);

  /** PS 廃止の確認ダイアログの表示・非表示状態。 */
  const [isConfirmDeletePsDialogOpen, setIsConfirmDeletePsDialogOpen] = useState<boolean>(false);

  // const getLoginUserPrivilegeResponse = useGetLoginUserprivilege(defaultOptions);

  // 「PS一覧 承認機能」を持つユーザーが PS 廃止可能とする (「PS一覧 承認機能」も廃止と同じ「システム管理者」「承認者（管理職）」だけが可能なため）。
  // 「PS一覧 承認機能」は /src/backend/app/app/Constants/Consts.php で
  // FUNC_ID_PSLI_APP = 45
  // と定義されている。
  // /auth/login/privilegeInfo?function_id=45
  // のように呼び出す。
  /** 権限取得 API 呼び出し。 */
  const getLoginPrivilegeInfoResponse = useGetLoginPrivilegeInfo({ function_id: 45 }, defaultOptions);

  // console.log('getLoginPrivilegeInfoResponse.data.data.user_privileges', getLoginPrivilegeInfoResponse.data?.data.user_privileges);

  // if (getLoginUserPrivilegeResponse.isLoading) {
  //   return (
  //     <div className='master-navi'>
  //       {/* <ul /> */}
  //     </div>
  //   );
  // }

  if (getLoginPrivilegeInfoResponse.isError) {
    return (
      <div className="master-navi">
        <ErrorComponent error={getLoginPrivilegeInfoResponse.error} />
      </div>
    );
  }

  return (
    <>
      <div className="master-navi">
        <ul>
          {
            // 閲覧画面の場合。
            mode === 'View' && (
              <>
                <li
                  className="master-navibtn1 modal-open"
                  onClick={() => {
                    // (PS ツリー) 表示オプション ダイアログ表示。
                    setIsPsTreeDisplayOptionsDialogOpen(true);
                  }}
                >
                  <i className="fa-solid fa-eye" />
                  {t('表示オプション')}
                </li>
                <li className="master-navibtn2">
                  {[t('日本語'), t('米語'), t('英語')].map((language: string, languageIndex: number) => (
                    <span
                      key={language}
                      className="ck-left2"
                      onClick={() => {
                        const newDisplayLanguages = Array.from(bulkDisplayLanguages);
                        newDisplayLanguages.splice(languageIndex, 1, !bulkDisplayLanguages[languageIndex]);
                        setBulkDisplayLanguages(newDisplayLanguages);
                      }}
                      style={{
                        marginRight: '0.5rem',
                      }}
                    >
                      <input type="checkbox" checked={bulkDisplayLanguages[languageIndex]} readOnly />
                      <label style={{ paddingTop: '1px' }}>{language}</label>
                    </span>
                  ))}
                </li>
                <li
                  className="master-navibtn2"
                  style={canPsDownload ? {} : { color: 'lightgray' }}
                  onClick={() => {
                    if (canPsDownload) {
                      setPsDownloadFileType(0);
                    }
                  }}
                >
                  <i className="fa-solid fa-download" />
                  {t('PS出力')}
                </li>
                <li
                  className="master-navibtn3"
                  style={canPsDownload ? {} : { color: 'lightgray' }}
                  onClick={() => {
                    if (canPsDownload) {
                      setPsDownloadFileType(1);
                    }
                  }}
                >
                  <i className="fa-solid fa-download" />
                  {t('原作用PS出力')}
                </li>
                {targetPs &&
                  // 廃止された PS は編集不可。
                  !targetPs.deleted_flag &&
                  // 承認済み最新バージョンと改廃案は編集可能、それら以外は不可とする。
                  (targetPs.latest || targetPs.state === 0) &&
                  // PS の改廃案状態 (temp_state) が、3 (確認待ち (G/L)) または 4 (確認待ち (管理職)) の場合は、ボタンを非表示にする。
                  targetPs.temp_state !== 3 &&
                  targetPs.temp_state !== 4 && (
                    <li className="master-navibtn4">
                      {/* TODO?: disable if targetPs is undefined */}
                      <Link
                        to={
                          Number.isInteger(targetPs?.ps_version_id)
                            ? `/${i18n.language}/ps/edit/${targetPs?.ps_version_id as number}`
                            : ''
                        }
                      >
                        <i className="fa-solid fa-pen-to-square" />
                        {t('編集')}
                      </Link>
                    </li>
                  )}
                <li>
                  {/* 表示している PS に紐付く PS 改廃案メモ一覧画面を別タブで表示する。 */}
                  <Link
                    to={
                      Number.isInteger(targetPs?.ps_version_id)
                        ? `/${i18n.language}/ps/view/${targetPs?.ps_version_id as number}/psnote`
                        : ''
                    }
                    target={Number.isInteger(targetPs?.ps_version_id) ? '_blank' : '_self'}
                  >
                    <i className="fa-solid fa-eye" />
                    {t('PS改廃案メモ表示')}
                  </Link>
                </li>
                {
                  // ログイン ユーザーが「システム管理者」「承認者（管理職）」権限を持つ場合のみ表示する。
                  // (権限は privileges テーブルで確認。)
                  // 「PS一覧 承認機能」を持つユーザーが PS 廃止可能とする (「PS一覧 承認機能」も廃止と同じ「システム管理者」「承認者（管理職）」だけが可能なため）。
                  // コンテンツ PS を廃止する場合「選択しているPSが廃止されます」、タイトル種別の PS を廃止する場合「直下のPSも同時に廃止されます」という確認ダイアログを表示する。
                  // 確認ダイアログで確認後、PS の廃止処理を実行する。（改廃案は作成せず即時に実行する。）
                  // ただし、対象 PS に改廃案が存在する場合、その状態が「確認待ち(G/L)」「承認待ち(管理職)」の場合は、削除出来ない旨のダイアログを表示し、削除処理を中断。
                  Array.isArray(getLoginPrivilegeInfoResponse.data?.data.user_privileges) &&
                    // accsess_restriction = 0 (○：編集/閲覧可) であれば PS 廃止可能。
                    // ユーザーには 2 つの権限が付与できるので、/auth/login/privilegeInfo ではそれぞれに対する設定値が返ってくる。1 つでも accsess_restriction = 0 があれば OK。
                    (getLoginPrivilegeInfoResponse.data?.data.user_privileges as UserPrivilege[]).some(
                      (userPrivilege) => userPrivilege.accsess_restriction === 0
                    ) &&
                    targetPs &&
                    // 既に廃止されていた場合、非表示にする。
                    !targetPs.deleted_flag &&
                    !isL0L1Ps && (
                      <li
                        style={{
                          // 右寄せ。
                          margin: '0 0 0 auto',
                        }}
                        onClick={() => {
                          if (targetPs) {
                            // 確認ダイアログ表示。
                            setIsConfirmDeletePsDialogOpen(true);
                          }
                        }}
                      >
                        <i className="fa-solid fa-circle-xmark" />
                        {t('廃止')}
                      </li>
                    )
                }
              </>
            )
          }
          {
            // PS 追加・編集画面の場合。
            (mode === 'Add' || mode === 'Edit') && (
              <li>
                {/* PS 追加画面の場合、親 PS の閲覧画面へ遷移する。 */}
                {/* PS 編集画面の場合、URL で指定された PS バージョンの閲覧画面へ遷移する。(改廃案がある場合でも、改廃案ではなく、承認済の最新版の PS バージョンを閲覧するため) */}
                <Link
                  to={`/${i18n.language}/ps/view/${toString(
                    mode === 'Add' ? targetPs?.parent_ps_version_id : psVersionIdParam
                  )}`}
                >
                  <i className="fa-solid fa-book-open" />
                  {t('閲覧')}
                </Link>
              </li>
            )
          }
        </ul>
      </div>

      {isConfirmDeletePsDialogOpen && (
        <ConfirmModal
          onClickOk={() => {
            // 確認ダイアログで確認後、PS の廃止処理を実行する。
            // API 呼び出し。
            postPsDeleteMutation.mutate({
              psVersionId: targetPs?.ps_version_id as number,
            });
            // このダイアログを閉じる。
            setIsConfirmDeletePsDialogOpen(false);
            // 「PS ツリーの再検索を実行」フラグをリセット。
            setDidSetDoRePostPsTreeSearch(false);
          }}
          onClickCancel={() => {
            // このダイアログを閉じる。
            setIsConfirmDeletePsDialogOpen(false);
          }}
        >
          <div>
            {t('表示中のPS情報を廃止しますか？')}
            <br />
            {
              // コンテンツ PS を廃止する場合「選択しているPSが廃止されます」という確認ダイアログを表示する。
              targetPs?.type === 3 && t('選択しているPSが廃止されます。')
            }
            {
              // タイトル種別の PS を廃止する場合「直下のPSも同時に廃止されます」という確認ダイアログを表示する。
              targetPs?.type === 2 && t('直下のPSも同時に廃止されます。')
            }
          </div>
        </ConfirmModal>
      )}

      {
        // 廃止中、SpinnerModal を表示。
        postPsDeleteMutation.isLoading && <SpinnerModal />
      }

      {
        // 廃止成功メッセージを表示。
        postPsDeleteMutation.isSuccess && <OkModal>{t('PS情報の廃止に成功しました。')}</OkModal>
      }

      {
        // TODO: 確認。
        // 廃止エラー メッセージを表示。
        postPsDeleteMutation.isError && (
          <ErrorModal error={postPsDeleteMutation.error}>
            {t('PS情報の廃止に失敗しました。管理者に打ち上げください。')}
            <br />
            <br />
          </ErrorModal>
        )
      }

      {psDownloadFileType >= 0 && (
        <ConfirmModal
          onClickOk={() => {
            // 確認ダイアログで確認後、PS 出力を実行する。

            resetPsDownloadBatchStates();

            // PS 出力中。
            setIsPsDownloadBatchInProgress(true);

            // API 呼び出し。
            postPsDownloadAddMutation.mutate({
              //  /**
              //   * PSダウンロード条件
              //   */
              //  export interface PsDownloadCondition {
              //    /** 出力ファイル種別(0:PS出力,1:原作用PS出力) */
              //    type?: number;
              //    /** PSバージョンIDの配列 */
              //    ps_version_ids?: number[];
              //  }
              data: {
                type: psDownloadFileType,
                language_id: sessionStorageUtils.get('PsTree.LanguageId', 0),
                ps_version_ids: psVersionIdsToDownload,
              },
            });

            // このダイアログを閉じる。
            setPsDownloadFileType(-1);
          }}
          onClickCancel={() => {
            // このダイアログを閉じる。
            setPsDownloadFileType(-1);
          }}
        >
          <div>{t('Excelファイルの出力を実行してもよろしいですか？')}</div>
        </ConfirmModal>
      )}

      {
        // PS 情報 Excel ファイル生成バッチ処理ジョブ登録 API がエラー。
        postPsDownloadAddMutation.isError && (
          <ErrorModal error={postPsDownloadAddMutation.error}>
            {t('Excelファイルの出力に失敗しました。管理者に打ち上げください。')}
            <br />
            <br />
          </ErrorModal>
        )
      }

      {/* PS 出力中ダイアログ。 */}
      <Modal
        active={isPsDownloadBatchInProgress && !isReadyForDownloadPs}
        additionalClassName="popupfr340"
        displayCloseButton={false}
        shouldCloseOnOverlayClick={false}
      >
        <div>{t('処理中')}</div>
        <hr
          style={{
            border: '1px solid WhiteSmoke',
          }}
        />
        <div>
          <div
            style={{
              margin: '1rem 0',
            }}
          >
            {t('Excelファイルを出力しています…')}
          </div>
          <div
            className="center"
            style={{
              margin: '1rem 0',
            }}
          >
            <i className="fas fa-spinner fa-spin" />
          </div>
        </div>
        <hr
          style={{
            border: '1px solid WhiteSmoke',
          }}
        />
        <div
          className="mb0 center"
          style={{
            marginTop: '1rem',
          }}
        >
          <button
            type="button"
            className="red-button"
            onClick={() => {
              if (typeof psExportBatchId === 'string') {
                // PS 情報 Excel 生成バッチ処理キャンセル API を呼ぶ。
                postPsDownloadCancelMutation.mutate({
                  batchId: psExportBatchId,
                });
              }
              // API の成功 / 失敗は気にしない。

              // states をリセット。
              resetPsDownloadBatchStates();
            }}
          >
            {t('キャンセル')}
          </button>
        </div>
      </Modal>

      {isReadyForDownloadPs && (
        <OkModal
          onClickOk={() => {
            // states をリセット。
            resetPsDownloadBatchStates();
          }}
        >
          {t('Excelファイルの出力が完了しました。')}
        </OkModal>
      )}

      {
        // PS 情報 Excel ファイル ダウンロード チェック API がエラー。
        postPsDownloadCheckMutation.isError && (
          <ErrorModal error={postPsDownloadCheckMutation.error}>
            {t('Excelファイルの出力に失敗しました。管理者に打ち上げください。')}
            <br />
            <br />
          </ErrorModal>
        )
      }

      {
        // PS 情報 Excel ファイル ダウンロード API がエラー。
        postPsDownloadBatchMutation.isError && <BlobErrorModal error={postPsDownloadBatchMutation.error} />
      }
    </>
  );
}

export default PsDetailsPageToolbar;
